// Guest Valet Parking — "Bring my car". Room is attached automatically via
// the existing room session; the VALET TICKET is the vehicle's identifier.
import { useState, useEffect, useCallback } from 'react';
import { ScreenShell, TipNotice, DEFAULT_SURFACE } from './AssistantScreens';
import { Car, CheckCircle2, Clock, Loader2 } from 'lucide-react';

const chip = (active) =>
    `px-3.5 py-2 rounded-full text-sm font-medium transition-colors border ${active
        ? 'bg-emerald-600 border-emerald-500 text-white'
        : 'bg-white/5 border-white/15 text-slate-300 hover:bg-white/10'}`;

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;
const inputCls = 'w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500';

const StatusPill = ({ status, tx }) => {
    const styles = {
        retrieval_requested: 'text-amber-300 bg-amber-500/15 border-amber-400/30',
        retrieving: 'text-sky-300 bg-sky-500/15 border-sky-400/30',
        ready: 'text-emerald-300 bg-emerald-500/15 border-emerald-400/30',
        delivered: 'text-slate-300 bg-white/10 border-white/20',
        cancelled: 'text-rose-300 bg-rose-500/15 border-rose-400/30',
    };
    return (
        <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${styles[status] || styles.delivered}`}>
            {tx[`valet_${status}`] || status}
        </span>
    );
};

export default function ValetScreen({ cfg, tx, api, cardId, token, post, onBack, surface }) {
    const sf = surface || DEFAULT_SURFACE;
    const [records, setRecords] = useState(null);
    const [form, setForm] = useState(false);
    const [when, setWhen] = useState(null); // minutes number | 'time'
    const [timeVal, setTimeVal] = useState('');
    const [fields, setFields] = useState({ guest_name: '', make: '', model: '', color: '', plate: '' });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [err, setErr] = useState('');

    const load = useCallback(() => {
        fetch(`${api}/api/assistant/${cardId}/valet-requests?token=${encodeURIComponent(token)}`)
            .then((r) => (r.ok ? r.json() : { requests: [] }))
            .then((d) => setRecords(d.requests || []))
            .catch(() => setRecords([]));
    }, [api, cardId, token]);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setFields({ ...fields, [k]: e.target.value });
    const REL = [{ m: 0, label: tx.now }, { m: 15, label: tx.in15 }, { m: 30, label: tx.in30 }, { m: 60, label: tx.in1h }];
    const whenOk = when === 'time' ? TIME_RE.test(timeVal) : typeof when === 'number';
    const canSubmit = whenOk && fields.guest_name.trim() && fields.make.trim() && fields.model.trim();

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setErr('');
        try {
            const body = { ...fields, ...(when === 'time' ? { at_time: timeVal } : { minutes: when }) };
            const d = await post('valet-request', body);
            setDone(d);
            load();
        } catch (e) { setErr(tx.valetError); }
        finally { setBusy(false); }
    };

    const tip = cfg.tip_enabled && <TipNotice label={tx.tipValet} tx={tx} post={post} recipient="valet" testId="assistant-tip-valet" />;

    if (done) return (
        <ScreenShell title={tx.valet} onBack={onBack} tx={tx} testId="assistant-valet-screen">
            <div className={`${sf} rounded-2xl p-6 text-center`} data-testid="assistant-valet-sent">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                <p className="text-white font-semibold mt-3">{tx.valetSentTitle}</p>
                <p className="text-slate-400 text-xs mt-3 uppercase tracking-widest">{tx.valetTicket}</p>
                <p className="text-emerald-300 font-bold text-3xl mt-1" data-testid="assistant-valet-ticket">#{done.ticket}</p>
                <p className="text-slate-300 text-sm mt-3">{tx.valetSentBody} {done.requested_for}</p>
                <button type="button" onClick={() => { setDone(null); setForm(false); }}
                    className="mt-5 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                    data-testid="assistant-valet-done-btn">
                    {tx.done}
                </button>
            </div>
            {tip}
        </ScreenShell>
    );

    return (
        <ScreenShell title={tx.valet} onBack={onBack} tx={tx} testId="assistant-valet-screen">
            {records === null && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}

            {records !== null && records.length > 0 && !form && (
                <div className="space-y-3 mb-4" data-testid="assistant-valet-records">
                    {records.map((r) => (
                        <div key={r.id} className={`${sf} rounded-2xl p-4`} data-testid={`assistant-valet-record-${r.ticket}`}>
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-white font-semibold text-sm">#{r.ticket} — {[r.vehicle?.color, r.vehicle?.make, r.vehicle?.model].filter(Boolean).join(' ')}</p>
                                <StatusPill status={r.status} tx={tx} />
                            </div>
                            <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" /> {tx.valetRequestedFor}: {r.requested_for}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {records !== null && !form && (
                <button type="button" onClick={() => setForm(true)}
                    className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors inline-flex items-center justify-center gap-2"
                    data-testid="assistant-valet-bring-btn">
                    <Car className="w-5 h-5" /> {records.length > 0 ? tx.valetAnother : tx.bringMyCar}
                </button>
            )}

            {form && (
                <div className={`${sf} rounded-2xl p-5`} data-testid="assistant-valet-form">
                    <p className="text-white font-semibold mb-1">{tx.bringMyCar}</p>
                    <p className="text-slate-400 text-sm mb-3">{tx.valetWhen}</p>
                    <div className="flex flex-wrap gap-2" data-testid="assistant-valet-when">
                        {REL.map((o) => (
                            <button key={o.m} type="button" onClick={() => setWhen(o.m)} className={chip(when === o.m)}
                                data-testid={`assistant-valet-when-${o.m}`}>{o.label}</button>
                        ))}
                        <button type="button" onClick={() => setWhen('time')} className={chip(when === 'time')}
                            data-testid="assistant-valet-when-time">{tx.untilTime}</button>
                    </div>
                    {when === 'time' && (
                        <input type="time" value={timeVal} onChange={(e) => setTimeVal(e.target.value)}
                            className={`${inputCls} mt-3`} data-testid="assistant-valet-time-input" />
                    )}
                    <div className="space-y-2.5 mt-4">
                        <input value={fields.guest_name} onChange={set('guest_name')} placeholder={`${tx.guestName} *`}
                            className={inputCls} data-testid="assistant-valet-name-input" />
                        <div className="grid grid-cols-2 gap-2.5">
                            <input value={fields.make} onChange={set('make')} placeholder={`${tx.vehicleMake} *`}
                                className={inputCls} data-testid="assistant-valet-make-input" />
                            <input value={fields.model} onChange={set('model')} placeholder={`${tx.vehicleModel} *`}
                                className={inputCls} data-testid="assistant-valet-model-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                            <input value={fields.color} onChange={set('color')} placeholder={tx.vehicleColor}
                                className={inputCls} data-testid="assistant-valet-color-input" />
                            <input value={fields.plate} onChange={set('plate')} placeholder={tx.vehiclePlate}
                                className={inputCls} data-testid="assistant-valet-plate-input" />
                        </div>
                        <p className="text-slate-500 text-[11px]">{tx.valetOptionalHint}</p>
                    </div>
                    {err && <p className="text-rose-400 text-xs mt-2" data-testid="assistant-valet-error">{err}</p>}
                    <button type="button" onClick={submit} disabled={!canSubmit || busy}
                        className="mt-4 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                        data-testid="assistant-valet-submit-btn">
                        {busy ? tx.sending : tx.valetSubmit}
                    </button>
                </div>
            )}
            {tip}
        </ScreenShell>
    );
}
